import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deletePlan,
  getPlanKpis,
  getPlans,
  PlanDeleteImpact,
  PlanFilters,
  PlanListItem,
} from '@/api/plansService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const PAGE_SIZE = 30;
const ARCHIVE_KEY = 'ADMIN_PV2_ARCHIVED_PLANS';

export type PlanStatus = 'Publié' | 'Brouillon' | 'Archivé';

const readArchived = (): Set<string> => {
  try {
    const raw = localStorage.getItem(ARCHIVE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const writeArchived = (ids: Set<string>) => {
  try {
    localStorage.setItem(ARCHIVE_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
};

const errorDescription = (error: unknown): string => {
  const res = (error as { response?: { data?: { message?: string; impact?: PlanDeleteImpact } } })?.response;
  if (res?.data?.message) return res.data.message;
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

export const usePlans = (filters: { search?: string; format?: string; planType?: string; page: number }) => {
  const queryClient = useQueryClient();
  const [archived, setArchived] = useState<Set<string>>(readArchived);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'plans'], refetchType: 'all' });

  const listFilters: PlanFilters = {
    search: filters.search,
    format: filters.format,
    planType: filters.planType,
    page: filters.page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2Plans(filters),
    queryFn: () => getPlans(listFilters),
  });

  const { data: kpis } = useQuery({
    queryKey: queryKeys.pv2PlanKpis,
    queryFn: getPlanKpis,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePlan(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2PlanKpis, refetchType: 'all' });
      toaster.create({ title: 'Plan supprimé', type: 'success' });
    },
    onError: (error) =>
      toaster.create({ title: 'Suppression impossible', description: errorDescription(error), type: 'error' }),
  });

  const isArchived = useCallback((id: string) => archived.has(id), [archived]);

  const toggleArchive = useCallback((id: string) => {
    setArchived((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeArchived(next);
      return next;
    });
  }, []);

  const statusOf = useCallback(
    (plan: PlanListItem): PlanStatus => {
      if (archived.has(plan.id)) return 'Archivé';
      return plan.cyclesCount > 0 ? 'Publié' : 'Brouillon';
    },
    [archived]
  );

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    kpis,
    isLoading,
    deletePlan: deleteMutation.mutateAsync,
    invalidate,
    isArchived,
    toggleArchive,
    statusOf,
  };
};
